import { SelectBox } from "devextreme-react";
import Validator, { CustomRule, RequiredRule } from 'devextreme-react/validator';
import { Dimensions } from "../../Enums/dimensions";
import "./BaseSelectField.scss";

const { Heights: { inputHeight } } = Dimensions

/**
 * 
 * @param BaseInputSelectField 
 * This is the base of a BaseInputSelectField for RMS.
 * It uses a SelectBox as the root UI Component from DevExtreme, and is used 
 * strings of text to store options of values that can be selected, one at time.
 * 
 */
/**
 * @param {Object} props
 * @param {Array | Object | any | null} props.data - Binds the UI component to a collection of data that contains String, Number, or Boolean values. Also accepts a DataSource (see DevExtreme documentation). Defaults to null.
 * @param {String | Function} props.displayExpr - Specifies the data field whose values should be, based on key-value pairs with valueExpr. Required only if `Object` is the type for dataSource. Defaults to undefined. Returns a string.
 * @param {String | Number | Function} props.height - Specifies the UI component's height. Defaults to undefined.
 * @param {Object} props.inputAttribute - Sets any global HTML attributes to the component. Optional. Defaults to {}.
 * @param {Boolean} props.isValid - Specifies or indicates if a field's value is valid (for required fields). Optional. Defaults to true.
 * @param {String} props.label - The visible label (text) for the button. Defaults to "".
 * @param {String} props.labelMode - Specifies how a label should persist: "static", "floating", or "hidden". Defaults to "static".
 * @param {String} props.message - The message shown if a validation rule is broken. Required if using the Validator API. Defaults to "Value is invalid.".
 * @param {Function} props.onEnterKey - A function executed when the Enter key is pressed while the component is focused. Optional. Defaults to null.
 * @param {Function} props.onInitialized - A function used to save the instance of the UI component, often for validation logic. Optional. Defaults to null.
 * @param {Function} props.onOptionChanged - A function executed after a UI component property changes. Optional. Defaults to null.
 * @param {Function} props.onValueChanged - A function that updates the state of the text field with a new value.
 * @param {Boolean} props.reevaluate - Indicates if validation should be re-checked every time or only on value change. Defaults to false.
 * @param {Function} props.validationCallback - Custom validation function. Returns true if valid, false otherwise. Required when using Custom Rule API. Defaults to null.
 * @param {Boolean} props.enableSearch - Specifies whether a user is allowed to search for values. Optional. Defaults to false.
 * @param {String} props.value - Sets the initial value of the text field. Defaults to "".
 * @param {String | Function} props.valueExpr - Field providing unique values for the component. Required only if `Object` is the type for dataSource. Defaults to 'this'.
 * @param {Boolean} props.readOnly - Sets component to read-only when true.
 */

export const BaseSelectField = (

    {
        data,
        enableSearch,
        inputAttribute,
        label,
        labelMode,
        onEnterKeyDown,
        onInitialized,
        onOptionChanged,
        optionsLabel,
        optionUpdatedByUser,
        requireValidation = true,
        validationRuleCallback,
        value,
        valueExpr,
        isVisible,
        noDataText,
        readOnly
    }
) => {
    return (
        <span className="inline__span--select-wrapper">
            <SelectBox
                id={inputAttribute?.id}
                dataSource={data}
                displayExpr={optionsLabel}
                height={inputHeight}
                isValid={value !== '' ? true : false}
                inputAttr={{ ...inputAttribute, id: inputAttribute?.id }}
                label={label}
                labelMode={labelMode}
                onEnterKey={onEnterKeyDown}
                onSelectionChanged={optionUpdatedByUser}
                searchEnabled={enableSearch}
                value={value}
                valueExpr={valueExpr}
                visible={isVisible ?? true}
                readOnly={readOnly}
            >
                {
                    requireValidation &&
                    <Validator
                        onInitialized={onInitialized}
                        onOptionChanged={onOptionChanged}
                    >
                        <RequiredRule type="required" />
                        <CustomRule
                            message="This field cannot be empty. Please provide a value."
                            reevaluate={true}
                            validationCallback={validationRuleCallback}
                        />
                    </Validator>
                }
            </SelectBox>
        </span>
    );
};